import React, { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiSearch, FiX, FiPlus } from "react-icons/fi";
import { crossSellingService } from "../../../../../../../services/crossSellingService";
import { CrossSellingMenuItem } from "../../../../../../../types/crossSelling";

interface CrossSellingTabProps {
  branchId: string;
  triggerItemId: string;
  selectedSuggestions: string[];
  onSuggestionsChange: (items: string[]) => void;
  onOriginalSuggestionsLoaded?: (items: string[]) => void;
}

const MAX_SELECTIONS = 3;

const CrossSellingTab: React.FC<CrossSellingTabProps> = ({
  branchId,
  triggerItemId,
  selectedSuggestions,
  onSuggestionsChange,
  onOriginalSuggestionsLoaded,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const searchRef = useRef<HTMLDivElement>(null);

  const [menuItems, setMenuItems] = useState<CrossSellingMenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Fetch menu items on mount
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        setIsLoading(true);
        const items = await crossSellingService.getBranchMenuItems(branchId);
        // Exclude the trigger item from the list
        const filteredItems = items.filter((item) => item.id !== triggerItemId);
        setMenuItems(filteredItems);
        setError(null);
      } catch (err) {
        setError(t("common.errorLoading", "Failed to load items"));
      } finally {
        setIsLoading(false);
      }
    };

    // Load existing selections
    const loadExistingSuggestions = async () => {
      try {
        const existing = await crossSellingService.getManualCrossSellingByItem(
          branchId,
          triggerItemId
        );
        if (existing.length > 0 && selectedSuggestions.length === 0) {
          onSuggestionsChange(existing);
          // Notify parent about original selections for delta calculation
          onOriginalSuggestionsLoaded?.(existing);
        }
      } catch (err) {
        console.error("Failed to load existing suggestions:", err);
      }
    };

    if (branchId) {
      fetchMenuItems();
      loadExistingSuggestions();
    }
  }, [branchId, triggerItemId, t]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get selected items with full data
  const selectedItems = useMemo(() => {
    return selectedSuggestions
      .map((id) => menuItems.find((item) => item.id === id))
      .filter((item): item is CrossSellingMenuItem => item !== undefined);
  }, [selectedSuggestions, menuItems]);

  // Filter items for search dropdown (exclude already selected)
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    return menuItems
      .filter((item) => !selectedSuggestions.includes(item.id))
      .filter((item) => {
        const enName = item.name?.toLowerCase() || "";
        const arName = item.ar?.name?.toLowerCase() || "";
        return enName.includes(query) || arName.includes(query);
      })
      .slice(0, 10); // Limit to 10 results
  }, [menuItems, searchQuery, selectedSuggestions]);

  // Get display name based on language
  const getDisplayName = (item: CrossSellingMenuItem) => {
    if (isRTL && item.ar?.name) {
      return item.ar.name;
    }
    return item.name;
  };

  // Handle adding an item
  const handleAddItem = (itemId: string) => {
    if (selectedSuggestions.length < MAX_SELECTIONS) {
      onSuggestionsChange([...selectedSuggestions, itemId]);
      setSearchQuery("");
      setIsDropdownOpen(false);
    }
  };

  // Handle removing an item
  const handleRemoveItem = (itemId: string) => {
    onSuggestionsChange(selectedSuggestions.filter((id) => id !== itemId));
  };

  const isMaxReached = selectedSuggestions.length >= MAX_SELECTIONS;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/3" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with description and counter */}
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2`}>
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {t("menuItems.crossSelling")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("menuItems.crossSellingDescription")}
          </p>
        </div>
        <div
          className={`px-3 py-1.5 rounded-full text-sm font-medium ${
            isMaxReached
              ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
          }`}
        >
          {selectedSuggestions.length}/{MAX_SELECTIONS}
        </div>
      </div>

      {/* Search input with dropdown */}
      {!isMaxReached && (
        <div ref={searchRef} className="relative">
          <div className={`absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none`}>
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t("menuItems.searchToAdd", "Search to add items...")}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            className="w-full ps-10 pe-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setIsDropdownOpen(false);
              }}
              className="absolute inset-y-0 end-0 pe-3 flex items-center"
            >
              <FiX className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}

          {/* Search Results Dropdown */}
          {isDropdownOpen && searchQuery && (
            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.length === 0 ? (
                <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                  {t("common.noResults", "No items found")}
                </div>
              ) : (
                searchResults.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleAddItem(item.id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={getDisplayName(item)}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400 text-xs">{t("menuItems.noImage")}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0 text-start">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {getDisplayName(item)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        ${item.price?.toFixed(2) || "0.00"}
                      </p>
                    </div>
                    <FiPlus className="w-5 h-5 text-primary flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Max reached notice */}
      {isMaxReached && (
        <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <p className="text-sm text-orange-700 dark:text-orange-400">
            {t("menuItems.maxReached")}
          </p>
        </div>
      )}

      {/* Selected Items List */}
      <div className="space-y-2">
        {selectedItems.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
            <p className="text-gray-500 dark:text-gray-400">
              {t("menuItems.noSelectedItems", "No items selected. Search above to add.")}
            </p>
          </div>
        ) : (
          selectedItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
            >
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt={getDisplayName(item)}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-400 text-xs">{t("menuItems.noImage")}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {getDisplayName(item)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  ${item.price?.toFixed(2) || "0.00"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveItem(item.id)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors flex-shrink-0"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CrossSellingTab;
